import { useMemo, useState } from 'react';
import type { FilterGroup } from '@practium/ui';
import { useTableList } from '../../hooks/useTableList';
import type { PlanNutrient, PlanSectionProps, PlanUpdate } from '../../types/parameters.types';
import { applyScale, formatNumber } from '../../utils/format';
import { ValuesFormModal } from '../Forms/ValuesFormModal';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';

export interface NutrientRow {
	id: string;
	title: string;
	meta?: string;
	tags?: string[];
	values: number[];
	isPercent?: boolean;
	toUpdate: (values: number[]) => PlanUpdate;
}

interface NutrientRowsSectionProps extends PlanSectionProps {
	rows: NutrientRow[];
	titleHeader: string;
	metaHeader?: string;
	unit: [string, string];
	filterGroups?: FilterGroup[];
	searchPlaceholder?: string;
}

const getFilterTags = (row: NutrientRow) => row.tags ?? [];

const getSearchText = (row: NutrientRow) => `${row.title} ${row.meta ?? ''}`;

export const NutrientRowsSection = ({
	plan,
	isSaving,
	onSave,
	rows,
	titleHeader,
	metaHeader,
	unit,
	filterGroups,
	searchPlaceholder,
}: NutrientRowsSectionProps) => {
	const [editing, setEditing] = useState<NutrientRow | null>(null);
	const nutrients: PlanNutrient[] = plan.nutrientOrder;
	const list = useTableList({
		items: rows,
		filterGroups,
		getFilterTags,
		getSearchText: searchPlaceholder ? getSearchText : undefined,
		pageSize: 12,
	});

	const columns = useMemo<ReferenceColumn<NutrientRow>[]>(
		() => [
			{
				key: 'title',
				header: titleHeader,
				render: (row) => <span className={tableStyles.cellPrimary}>{row.title}</span>,
			},
			...(metaHeader
				? [
						{
							key: 'meta',
							header: metaHeader,
							render: (row: NutrientRow) => <span className={tableStyles.cellSecondary}>{row.meta}</span>,
						},
					]
				: []),
			...nutrients.map((nutrient, index) => ({
				key: nutrient,
				header: nutrient,
				render: (row: NutrientRow) => (
					<span className={tableStyles.cellValue}>
						{row.isPercent
							? `${formatNumber(applyScale(row.values[index], 100), 2)}%`
							: formatNumber(row.values[index], 4)}
					</span>
				),
			})),
		],
		[metaHeader, nutrients, titleHeader]
	);

	return (
		<>
			<SectionList
				list={list}
				columns={columns}
				getRowId={(row) => row.id}
				unit={unit}
				searchPlaceholder={searchPlaceholder}
				onEdit={setEditing}
			/>
			{editing && (
				<ValuesFormModal
					title={`Editar: ${editing.title}`}
					subtitle={editing.meta}
					sectionTitle="Valores por nutriente"
					fields={nutrients.map((nutrient) => ({ key: nutrient, label: nutrient }))}
					initialValues={Object.fromEntries(nutrients.map((nutrient, index) => [nutrient, editing.values[index]]))}
					scale={editing.isPercent ? 100 : 1}
					max={editing.isPercent ? 1 : undefined}
					maxMessage={editing.isPercent ? 'Use um valor entre 0 e 100%.' : undefined}
					suffix={editing.isPercent ? '%' : undefined}
					isSaving={isSaving}
					onSubmit={(values) =>
						onSave(editing.toUpdate(nutrients.map((nutrient) => values[nutrient])), 'Valores salvos com sucesso!')
					}
					onClose={() => setEditing(null)}
				/>
			)}
		</>
	);
};
